/* eslint-disable filenames/match-regex, react-hooks/exhaustive-deps, react/forbid-component-props, complexity */
import { faCcVisa, faCcMastercard } from '@fortawesome/free-brands-svg-icons';
import {
  faCreditCard,
  faCalendarAlt,
  faMapMarkerAlt,
  faLock,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import dataCollector from 'braintree-web/data-collector';
import hostedFields from 'braintree-web/hosted-fields';
import threeDSecure from 'braintree-web/three-d-secure';
import { Formik } from 'formik';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { MERCHANT_NAME } from '../../../brands/default/config';
import {
  PAYMENT_TOP_CTA_ID,
  PAYMENT_TYPES,
  PAYMENT_METHODS,
  PAYMENT_DOD_TRACKING_CARD_STATUS,
  PAYMENT_DOD_TRACKING_GATEWAY,
  TYPE_PAYMENT,
} from '../../../brands/default/constants';
import {
  BUTTON_BUY_WITH_VIP,
  BECOME_A_VIP,
  CARD_ERROR_DATE_NOT_VALID,
  CARD_ERROR_INVALID,
  CARD_ERROR_TYPES,
  CARD_INPUT_DATA_QA,
  CARD_TOGGLE_SAVED_DATA_QA,
  CARD_TOGGLE_SAVED_LINK,
  CARD_TOGGLE_SAVED_PRE,
  CVV_ERROR_REQUIRED,
  DEFAULT_PAYMENT_ERROR,
  ERROR_MESSAGE_BRAINTREE,
  LIABILITY_ERROR,
  POSTAL_CODE_REQUIRED,
  VIP_WITHOUT_UNAVAILABLE,
  VIP_PAY_EXCLUSIVE_DEAL_MESSAGE,
  BUTTON_PLACE_ORDER,
} from '../../../brands/default/content/components/checkout';
import { trackEvent, updateDod } from '../../../helpers/analytics';
import BraintreeService from '../../../helpers/braintreeService';
import { formatPrice } from '../../../helpers/currency';
import ErrorTrackingService from '../../../helpers/errorTrackingService';
import Message from '../../_generic/Message';
import { logToSplunk } from '../../_generic/axiosSplunk/axiosSplunk';
import CTAButton from '../../_generic/button/CTAButton';
import PrimaryButton from '../../_generic/button/PrimaryButton';
import DummyInput from '../../_generic/form/DummyInput';
import WrappedInput from '../../_generic/form/WrappedInput';
import ClientOnlyPortal from '../../_generic/portal/ClientOnlyPortal';
import Spinner from '../../_generic/spinner/Spinner';
import useToast from '../../_generic/toast/UseToast';
import BasketSocialCues from '../BasketSocialCues';
import ButtonGoToPayment from './ButtonGoToPayment';
import CardFields from './CardFields';
import CardStyles from './CardStyles';
import PaymentAgreeTerms from './PaymentAgreeTerms';
import PaymentCardToggle from './PaymentCardToggle';
import PaymentSummary from './PaymentSummary';
import VipButtonContainer from './VipButtonContainer';
import VipMessage from './VipMessage';
import VipMessageWrapper from './VipMessageWrapper';
import VoucherContractDisclamer from './VoucherContractDisclamer';

const PaymentCardBT = ({
  afterError,
  atolProtected,
  basketProducts,
  currencySymbol,
  finalOrderAmount,
  hasSavedCard,
  isExclusiveProductInBasket,
  isVipUser,
  isWalletApplied,
  orderComplete,
  orderSummary,
  checkoutData,
  switchToSavedCard,
  threeDSCheckEnabled,
  totalQuantity,
  providerError,
  travelDisableButton,
  vipOptIn = false,
  vipOptInFinalOrderAmount,
  vipPreButtonMessage,
  vipSubscriptionMessage,
  welcomeVipEnabled,
  vipPaywall,
  isSecondCheckout,
}) => {
  const toast = useToast();
  const [isCardNumberValid, setIsCardNumberValid] = useState(false);
  const [isCardNumberInvalid, setIsCardNumberInvalid] = useState(false);
  const [isCardNumberNotAccepted, setIsCardNumberNotAccepted] = useState(false);
  const [isCVVValid, setIsCVVValid] = useState(false);
  const [isCVVInvalid, setIsCVVInvalid] = useState(false);
  const [isPostalCodeValid, setIsPostalCodeValid] = useState(false);
  const [isPostalCodeInvalid, setIsPostalCodeInvalid] = useState(false);
  const [isExpMonthValid, setIsExpMonthValid] = useState(false);
  const [isExpYearValid, setIsExpYearValid] = useState(false);
  const [isExpMonthInvalid, setIsExpMonthInvalid] = useState(false);
  const [isExpYearInvalid, setIsExpYearInvalid] = useState(false);
  const [cardIcon, setCardIcon] = useState(faCreditCard);
  const [paymentInProcess, setPaymentInProcess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [dataCollectorInstance, setDataCollectorInstance] = useState(null);
  const [validPayment, setValidPayment] = useState(false);
  const [instance, setInstance] = useState(null);
  const [threeDSecureInstance, setThreeDSecureInstance] = useState(null);
  const [clientInstance, setClientInstance] = useState(null);
  const userStore = useSelector((state) => state.user);
  const postCode = userStore.userprofile.postcode || null;

  const control = {
    cvv: {
      dirty: false,
      invalid: false,
    },
    number: {
      dirty: false,
      invalid: false,
    },
    postalCode: {
      dirty: false,
      invalid: false,
    },
  };

  const notifyError = (message) => {
    toast.addToast(message, 'toast-error', 'top-right');
  };

  const validateCardType = (cardTypeName, valid) => {
    if (cardTypeName === 'visa' && valid) {
      setCardIcon(faCcVisa);
      setIsCardNumberValid(true);
      setIsCardNumberNotAccepted(false);
    } else if (cardTypeName === 'master-card' && valid) {
      setCardIcon(faCcMastercard);
      setIsCardNumberValid(true);
      setIsCardNumberNotAccepted(false);
    } else {
      setCardIcon(faCreditCard);
      setIsCardNumberValid(false);
      setIsCardNumberNotAccepted(valid);
    }
  };

  const handleError = (error, message = DEFAULT_PAYMENT_ERROR) => {
    setIsLoading(false);
    setPaymentInProcess(false);
    notifyError(message);
    ErrorTrackingService.logError(error);
  };

  const validateCVV = (value) => {
    setIsCVVValid(value);
  };

  const validateMonthDate = (value) => {
    setIsExpMonthValid(value);
  };

  const validateYearDate = (value) => {
    setIsExpYearValid(value);
  };

  const validatePostalCode = (value) => {
    setIsPostalCodeValid(value);
  };

  const validateFields = (event) => {
    setValidPayment(
      Object.keys(event.fields).every((key) => event.fields[key].isValid),
    );
  };

  const cardNumberErrorText = () => {
    return isCardNumberInvalid
      ? CARD_ERROR_INVALID
      : '' || isCardNumberNotAccepted
      ? CARD_ERROR_TYPES
      : '';
  };

  const handleSwitchToSaved = () => {
    switchToSavedCard();
  };

  const handleOrderComplete = (order) => {
    if (dataCollectorInstance && dataCollectorInstance.teardown) {
      dataCollectorInstance.teardown();
    }
    setIsLoading(false);
    // call order complete in payment component
    orderComplete(order);
  };

  const handleOrderError = (error) => {
    if (dataCollectorInstance && dataCollectorInstance.teardown) {
      dataCollectorInstance.teardown();
    }
    // will reload the checkout
    setIsLoading(false);
    setPaymentInProcess(false);
    notifyError(DEFAULT_PAYMENT_ERROR);
    ErrorTrackingService.logError(error);
    afterError();
  };

  const storeWallet = () => {
    BraintreeService.walletUsed = isWalletApplied;
  };

  const storePostage = () => {
    BraintreeService.postageDiscount = orderSummary.totalPostageDiscount;
  };

  const storeProducts = () => {
    if (!BraintreeService.isPostCheckout) {
      if (!basketProducts) throw new Error('Products are empty');

      BraintreeService.products = basketProducts;

      return BraintreeService.products;
    }

    return null;
  };

  const handleValidation = (event) => {
    // handles event validation for the hosted fields
    if (event.emittedBy === 'postalCode') {
      control.postalCode.dirty = true;
    }
    if (event.emittedBy === 'cvv') control.cvv.dirty = true;
    if (event.emittedBy === 'number') control.number.dirty = true;

    setIsExpMonthInvalid(
      !event.fields.expirationMonth.isPotentiallyValid &&
        !event.fields.expirationMonth.isEmpty,
    );
    setIsExpYearInvalid(
      !event.fields.expirationYear.isPotentiallyValid &&
        !event.fields.expirationYear.isEmpty,
    );
    setIsCardNumberInvalid(
      (!event.fields.number.isPotentiallyValid ||
        (!event.fields.number.isFocused && !event.fields.number.isValid)) &&
        !event.fields.number.isEmpty &&
        control.number.dirty,
    );
    setIsPostalCodeInvalid(
      control?.postalCode?.dirty &&
        !event.fields.postalCode.isValid &&
        !event.fields.postalCode.isEmpty,
    );
    setIsCVVInvalid(
      control.cvv.dirty &&
        !event.fields.cvv.isValid &&
        !event.fields.cvv.isEmpty,
    );
    if (!event.fields.number.isEmpty) {
      validateCardType(event?.cards[0]?.type, event.fields.number.isValid);
    }
    validateCVV(event.fields.cvv.isValid && !event.fields.cvv.isEmpty);
    validateMonthDate(
      event.fields.expirationMonth.isValid &&
        !event.fields.expirationMonth.isEmpty,
    );
    validateYearDate(
      event.fields.expirationYear.isValid &&
        !event.fields.expirationYear.isEmpty,
    );
    validatePostalCode(
      event.fields.postalCode.isValid && !event.fields.postalCode.isEmpty,
    );
    validateFields(event);
  };

  const createHostedFields = async (newClientInstance) => {
    try {
      CardFields.postalCode.prefill = postCode;
      const cardInstance = await hostedFields.create({
        client: newClientInstance,
        fields: CardFields,
        styles: CardStyles,
      });
      setInstance(cardInstance);
      cardInstance.on('validityChange', (event) => {
        handleValidation(event);
      });
      cardInstance.on('blur', (event) => {
        handleValidation(event);
        if (event.emittedBy === 'cvv') {
          trackEvent('new_card_cvv_field');
        }
        if (event.emittedBy === 'number') {
          trackEvent('card_number_field');
        }
        if (event.emittedBy === 'expirationMonth') {
          trackEvent('card_expiry_month_field');
        }
        if (event.emittedBy === 'expirationYear') {
          trackEvent('card_expiry_year_field');
        }
        if (event.emittedBy === 'postalCode') {
          trackEvent('card_postcode_field');
        }
      });
      cardInstance.on('focus', (event) => {
        handleValidation(event);
      });
      setIsLoading(false);
    } catch (error) {
      if (
        error?.code !== 'HOSTED_FIELDS_FIELD_DUPLICATE_IFRAME' &&
        error?.code !== 'HOSTED_FIELDS_INVALID_FIELD_SELECTOR'
      ) {
        handleError(
          `Error creating Card Instance: ${error}`,
          DEFAULT_PAYMENT_ERROR,
        );
      } else {
        setIsLoading(false);
      }
    }
  };

  const setupMethods = async (newClientInstance) => {
    if (!newClientInstance) return;
    try {
      const response = await dataCollector.create({
        client: newClientInstance,
        kount: true,
        paypal: true,
      });

      if (response) {
        // At this point, you should access the dataCollectorInstance.deviceData value and provide it
        // to your server, e.g. by injecting it into your form as a hidden input.
        setDataCollectorInstance(response);
        BraintreeService.deviceData = response.deviceData;
      }

      createHostedFields(newClientInstance);
    } catch (error) {
      handleError(error, error.message);
    }
  };

  const setupProcessor = async (newClientInstance) => {
    if (!newClientInstance) return;
    if (threeDSCheckEnabled) {
      if (threeDSecureInstance) {
        threeDSecureInstance.teardown();
        setThreeDSecureInstance(null);
      }
      try {
        const response = await threeDSecure.create({
          client: newClientInstance,
          version: 2,
        });
        if (response) {
          setThreeDSecureInstance(response);
        } else {
          ErrorTrackingService.logError(DEFAULT_PAYMENT_ERROR);
          setIsLoading(false);
        }
      } catch (error) {
        handleError(error, error.message);
      }
    } else {
      setupMethods(newClientInstance);
    }
  };

  const setupBraintree = async () => {
    // payment component will be disabled if user is not logged in
    setIsLoading(true);
    BraintreeService.paymentComplete = handleOrderComplete;
    BraintreeService.paymentError = handleOrderError;
    BraintreeService.paymentType = PAYMENT_TYPES.card;
    BraintreeService.totalQuantity = totalQuantity;
    BraintreeService.providerError = providerError;
    try {
      const response = await BraintreeService.getClientInstance();
      setClientInstance(response);
      setupProcessor(response);
    } catch (error) {
      handleError(error, error.message);
    }
  };

  const sendPayment = (tokenizedPayload) => {
    BraintreeService.preparePayment({
      basketProducts,
      credit: false,
      dataCollectorInstance,
      isWalletApplied,
      orderSummary,
      paymentMethod: PAYMENT_METHODS.card,
      tokenizationPayload: tokenizedPayload,
    });
  };
  const submitPayment = async ({ orderAmount, tokenizedPayload }) => {
    if (threeDSecureInstance && threeDSCheckEnabled && orderAmount > 0) {
      threeDSecureInstance.on('lookup-complete', (data, next) => {
        next();
      });

      try {
        const payload = await threeDSecureInstance?.verifyCard({
          amount: orderAmount.toFixed(2).toString(),
          bin: tokenizedPayload.details.bin,
          merchantName: MERCHANT_NAME,
          nonce: tokenizedPayload.nonce,
        });
        if (payload.liabilityShiftPossible && !payload.liabilityShifted) {
          // Liablity possible, but failed challenge
          notifyError(LIABILITY_ERROR);
          setIsLoading(false);
        } else {
          // Either the liability shift is not possible, or the liability shifted
          if (payload.nonce) tokenizedPayload.nonce = payload.nonce;
          sendPayment(tokenizedPayload);
        }
      } catch (error) {
        handleError(error, error.message);
        setupBraintree();
      }
    } else if (orderAmount === 0) {
      sendPayment(tokenizedPayload);
    }
  };

  const tokenCreation = async ({ orderAmount }) => {
    try {
      const tokenizedPayload = await instance.tokenize({
        vault: true,
      });
      submitPayment({ orderAmount, tokenizedPayload });
    } catch (error) {
      handleError(
        `Error tokenizing Card details: ${error}`,
        ERROR_MESSAGE_BRAINTREE,
      );
    }
  };

  const preparePayment = (vipOptInValue) => {
    const orderAmount = vipOptInValue
      ? vipOptInFinalOrderAmount
      : finalOrderAmount;
    BraintreeService.vipOptIn = vipOptInValue;
    BraintreeService.totalAmountToPay = orderAmount;
    logToSplunk(
      {
        component: 'PaymentCardBt',
        type: 'Vip status',
        vipOptIn: vipOptInValue,
      },
      'frontend logs',
    );
    logToSplunk(
      {
        checkoutData,
        component: 'PaymentCardBt',
        isWalletAppliedBeforePayment: isWalletApplied,
        orderSummary,
        payment: 'New Card Payment',
        totalAmountTopay: orderAmount,
        type: 'payment data sent to payment providers',
        vipOptInFinalOrderAmount,
      },
      'frontend logs',
    );

    trackEvent('card_payment_click');
    if (welcomeVipEnabled && vipOptInValue) trackEvent('vip_cardbw_click');
    if (welcomeVipEnabled && !vipOptInValue) trackEvent('vip_cardbwo_click');
    trackEvent('New_Card_Braintree');
    updateDod({ card_status: PAYMENT_DOD_TRACKING_CARD_STATUS.new });
    updateDod({ gateway: PAYMENT_DOD_TRACKING_GATEWAY.braintree });
    setPaymentInProcess(true);
    setIsLoading(true);
    storeWallet();
    storePostage();
    storeProducts();
    tokenCreation({ orderAmount });
    providerError({});
  };

  useEffect(() => {
    if (threeDSecureInstance?.verifyCard) {
      setupMethods(clientInstance);
    }
  }, [threeDSecureInstance]);

  useEffect(() => {
    const timeOut = setTimeout(() => setupBraintree(), 100);

    return () => clearTimeout(timeOut);
  }, [threeDSCheckEnabled]);

  return (
    <>
      {isLoading && <Spinner isFullScreen />}
      <Formik
        enableReinitialize
        initialValues={{
          confirmPayment: true,
        }}
        onSubmit={(values, { setSubmitting }) => {
          setSubmitting(false);
        }}
        validate={(values) => {
          const errors = {};
          if (!values.confirmPayment) {
            errors.confirmPayment = true;
          }

          return errors;
        }}
      >
        {({
          isValid,
          handleSubmit,
          isSubmitting,
          /* and other goodies */
        }) => (
          <form
            className="row payment-body"
            name="payment"
            onSubmit={handleSubmit}
          >
            <div className="form-group col-12">
              {hasSavedCard && (
                <PaymentCardToggle
                  dataQa={CARD_TOGGLE_SAVED_DATA_QA}
                  linkAction={handleSwitchToSaved}
                  linkText={CARD_TOGGLE_SAVED_LINK}
                  preLinkText={CARD_TOGGLE_SAVED_PRE}
                />
              )}
            </div>
            <div className="col-12">
              <WrappedInput
                error={isCardNumberInvalid || isCardNumberNotAccepted}
                errorText={cardNumberErrorText()}
                formType={TYPE_PAYMENT}
                icon={cardIcon}
                normal
                valid={isCardNumberValid}
              >
                <DummyInput
                  controlClass="card-number"
                  dataqa={CARD_INPUT_DATA_QA}
                  id="card-number"
                />
              </WrappedInput>
            </div>
            <div className="col-6">
              <WrappedInput
                error={isExpMonthInvalid}
                errorText={CARD_ERROR_DATE_NOT_VALID}
                formType={TYPE_PAYMENT}
                icon={faCalendarAlt}
                normal
                valid={isExpMonthValid}
              >
                <DummyInput dataqa={CARD_INPUT_DATA_QA} id="expiration-month" />
              </WrappedInput>
            </div>
            <div className="col-6">
              <WrappedInput
                error={isExpYearInvalid}
                errorText={CARD_ERROR_DATE_NOT_VALID}
                formType={TYPE_PAYMENT}
                icon={faCalendarAlt}
                normal
                valid={isExpYearValid}
              >
                <DummyInput dataqa={CARD_INPUT_DATA_QA} id="expiration-year" />
              </WrappedInput>
            </div>
            <div className="col-12">
              <WrappedInput
                error={isCVVInvalid}
                errorText={CVV_ERROR_REQUIRED}
                formType={TYPE_PAYMENT}
                icon={faCreditCard}
                normal
                valid={isCVVValid}
              >
                <DummyInput
                  controlClass="card-number"
                  dataqa={CARD_INPUT_DATA_QA}
                  id="cvv"
                />
              </WrappedInput>
            </div>
            <div className="col-12">
              <WrappedInput
                error={isPostalCodeInvalid}
                errorText={POSTAL_CODE_REQUIRED}
                formType={TYPE_PAYMENT}
                icon={faMapMarkerAlt}
                normal
                valid={isPostalCodeValid}
              >
                <DummyInput
                  controlClass="card-number"
                  dataqa={CARD_INPUT_DATA_QA}
                  id="postal-code"
                />
              </WrappedInput>
            </div>
            <div className="col-12">
              <PaymentAgreeTerms disabled={false} />
              {!vipPaywall && (
                <BasketSocialCues
                  atolProtected={atolProtected}
                  className="with-margin"
                  isLighter
                  isPostCheckout={BraintreeService.isPostCheckout}
                />
              )}
              <VoucherContractDisclamer />
              <PaymentSummary
                {...orderSummary}
                isPostCheckout={BraintreeService.isPostCheckout}
                totalQuantity={totalQuantity}
                vipPaywall={vipPaywall}
              />
            </div>
            {(isVipUser || !welcomeVipEnabled) && (
              <div className="col-12">
                <CTAButton
                  className="full-width"
                  disabled={
                    travelDisableButton ||
                    paymentInProcess ||
                    isSubmitting ||
                    !validPayment ||
                    !isValid
                  }
                  onClick={() => preparePayment(vipOptIn)}
                  type="submit"
                >
                  <FontAwesomeIcon
                    icon={faLock}
                    style={{ height: 13, marginRight: '4px', width: 13 }}
                  />
                  {vipPaywall ? BECOME_A_VIP : BUTTON_PLACE_ORDER}
                </CTAButton>
              </div>
            )}
            {!isVipUser && welcomeVipEnabled && vipPreButtonMessage && (
              <VipMessageWrapper>{vipPreButtonMessage}</VipMessageWrapper>
            )}
            {!isVipUser && welcomeVipEnabled && (
              <div className="col-12">
                <VipButtonContainer big={!vipSubscriptionMessage}>
                  <PrimaryButton
                    className="less-rounded size-xlarger"
                    disabled={
                      travelDisableButton ||
                      paymentInProcess ||
                      isSubmitting ||
                      !validPayment ||
                      !isValid
                    }
                    onClick={() => preparePayment(true)}
                    type="submit"
                  >
                    <FontAwesomeIcon
                      icon={faLock}
                      style={{ height: 18, marginRight: '8px', width: 18 }}
                    />
                    {BUTTON_BUY_WITH_VIP}{' '}
                    <span className="pipe">&nbsp;|&nbsp;</span> {currencySymbol}
                    {formatPrice(vipOptInFinalOrderAmount, true, true, false)}
                  </PrimaryButton>
                  {vipSubscriptionMessage}
                </VipButtonContainer>
                {isExclusiveProductInBasket && (
                  <VipButtonContainer>
                    <Message message={VIP_PAY_EXCLUSIVE_DEAL_MESSAGE} />
                  </VipButtonContainer>
                )}
                {!isExclusiveProductInBasket && (
                  <VipButtonContainer>
                    <PrimaryButton
                      ariaDisabled={isExclusiveProductInBasket}
                      ariaDisabledReason={VIP_WITHOUT_UNAVAILABLE}
                      className="less-rounded light-blue-color size-xlarger"
                      disabled={
                        travelDisableButton ||
                        paymentInProcess ||
                        isSubmitting ||
                        !validPayment ||
                        !isValid
                      }
                      onClick={() => preparePayment(false)}
                      type="submit"
                    >
                      <FontAwesomeIcon
                        icon={faLock}
                        style={{
                          color: '#ffffff',
                          height: 16,
                          marginRight: '8px',
                          width: 16,
                        }}
                      />
                      {BUTTON_PLACE_ORDER}
                      <span className="pipe">&nbsp;|&nbsp;</span>{' '}
                      {currencySymbol}
                      {formatPrice(finalOrderAmount, true, true, false)}
                    </PrimaryButton>
                  </VipButtonContainer>
                )}
                <VipMessage />
              </div>
            )}
            {isSecondCheckout && vipSubscriptionMessage && (
              <div className="col-12">{vipSubscriptionMessage}</div>
            )}
          </form>
        )}
      </Formik>
      <ClientOnlyPortal targetElementId={PAYMENT_TOP_CTA_ID}>
        <ButtonGoToPayment />
      </ClientOnlyPortal>
    </>
  );
};

PaymentCardBT.propTypes = {
  afterError: PropTypes.func.isRequired,
  atolProtected: PropTypes.bool,
  basketProducts: PropTypes.array.isRequired,
  checkoutData: PropTypes.object,
  currencySymbol: PropTypes.string,
  finalOrderAmount: PropTypes.number.isRequired,
  hasSavedCard: PropTypes.bool.isRequired,
  isExclusiveProductInBasket: PropTypes.bool,
  isSecondCheckout: PropTypes.bool,
  isVipUser: PropTypes.bool,
  isWalletApplied: PropTypes.bool,
  orderComplete: PropTypes.func.isRequired,
  orderSummary: PropTypes.object.isRequired,
  providerError: PropTypes.func.isRequired,
  switchToSavedCard: PropTypes.func.isRequired,
  threeDSCheckEnabled: PropTypes.bool,
  totalQuantity: PropTypes.number,
  travelDisableButton: PropTypes.bool,
  vipOptIn: PropTypes.bool,
  vipOptInFinalOrderAmount: PropTypes.number,
  vipPaywall: PropTypes.bool,
  vipPreButtonMessage: PropTypes.element,
  vipSubscriptionMessage: PropTypes.element,
  welcomeVipEnabled: PropTypes.bool,
};

export default PaymentCardBT;
